import type { GrowthTemplateUnserialized, TemplateStepType } from 'livechat-settings-mfe/src/types/widget.types.ts'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { type Entry } from '../generated/sinch/chat/sdk/v1alpha2/resources_pb.ts'
import type { V1Channels, V1LiveChat, V1MultiChannelWidget } from '../generated/widget'
import type { EMBED_FEATURE, SinchEnvConfig, UserIdentityConfig, WIDGET_CHANNEL, WIDGET_MODULE_TYPE } from '../types.ts'
import { bigIntMiddleware } from './middleware/bigIntMiddleware.ts'
import { getIsAgentInConversation } from './utils/getIsAgentInConversation.ts'
import { setIsTypingWithTimeout } from './utils/setIsTypingWithTimeout.ts'

type LazyComponentsLoaded = Partial<Record<WidgetModule, boolean>>

type State = {
  widget?: V1MultiChannelWidget
  isPersistentStore: boolean
  region?: string // 'eu' | 'au'
  env?: string // 'stg' | 'prd'
  projectConfig?: SinchEnvConfig
  userIdentity?: UserIdentityConfig
  isPreview: boolean
  allIdentities: Record<string, string>
  embedFeature: EMBED_FEATURE | null
  // Whether the host's data-embed-target had no CSS-defined height at load
  // time (see sdk-loader.ts's pre-append measurement). When true, embed-mode
  // components stop forcing height:100% and instead size to their own
  // natural content height, which gets reported back to the loader so it
  // can grow the <iframe> itself to fit — see EmbedWidgetUI.tsx.
  embedAutoHeight: boolean
  isOpen: boolean
  showPrivacyCheck: boolean
  isStartScreenSubmitted: boolean
  showBannerContent: boolean
  showEndConversationOverlay: boolean
  attachments?: File[]
  isAvailable: boolean
  activeModule: WIDGET_MODULE_TYPE
  activeTemplate?: GrowthTemplateUnserialized
  activeTemplateStep: TemplateStepType
  // Sticky, NON-PERSISTED signal that the user has activated a module via a
  // real launcher gesture this page load (see markUserActivatedModule). Used
  // by useFocusFirstFocusableOnMount to gate module auto-focus so that a
  // collapsed load, an auto-open timer, or a persisted-open-on-load never
  // steals focus from the host page, while the user's first real open/switch
  // does focus. Excluded from persistence so it always starts false.
  hasUserActivatedModule: boolean
  lazyComponentsLoaded: LazyComponentsLoaded

  // message store
  entries?: Entry[]
  entryStatuses: Record<string, 'sending' | 'failed'>
  pendingOptimisticIds: string[]
  isTypingIndicator: boolean
  isAgentInConversation: boolean
  isConnected: boolean
  unreadCount: number

  //
  isInitialMessageSent: boolean
  isInitialAnimationDone: boolean
  // Audio functionality
  currentlyPlayingAudio: HTMLAudioElement | null

  // Enabled channels
  enabledSocialChannels: (keyof V1Channels)[]
  showStartAnimation?: boolean
  privacyUrl?: string

  // forwarding page channel
  forwardingPageChannel?: WIDGET_CHANNEL

  // forwarding
  selectedChannel?: keyof WIDGET_CHANNEL

  // Viewport: parent window dimensions in iframe mode, null in standalone
  parentViewportSize: { width: number; height: number } | null
}

type LiveModeActions = {
  subscribeToStream?: () => void // defined in runtime
  unsubscribeStream?: () => void // defined in runtime
  getHistory?: () => void // defined in runtime
}

export type WidgetModule = 'livechat' | 'growth' | 'forwarding-page' | 'sms'

type Actions = {
  setWidget: (widget: V1MultiChannelWidget) => void
  setLiveChatOption: <C extends keyof V1LiveChat, N extends V1LiveChat[C]>(name: C, value: N) => void
  setIsPersistentStore: (isPersistentStore: boolean) => void
  setRegion: (region: string) => void
  setEnv: (env: string) => void
  reset: () => void
  closeConversation: () => void
  setProjectConfig: (projectConfig: SinchEnvConfig) => void
  setPreview: (isPreview: boolean) => void
  setToken: (token: string) => void
  getToken: () => string | undefined
  setUserIdentity: (userIdentity?: UserIdentityConfig) => void
  // refreshAnonymousToken: () => Promise<void>;
  // refreshIdentity: () => Promise<void> | undefined;
  open: () => void
  close: () => void
  toggle: () => void
  setShowPrivacyCheck: (showPrivacyCheck: boolean) => void
  setIsStartScreenSubmitted: (isStartScreenSubmitted: boolean) => void
  setShowBannerContent: (showBannerContent: boolean) => void
  setShowEndConversationOverlay: (showEndConversationOverlay: boolean) => void
  setAttachments: (attachments?: File[]) => void
  setIsAvailable: (isAvailable: boolean) => void
  setEnabledSocialChannels: (enabledSocialChannels: (keyof V1Channels)[]) => void
  setShowStartAnimation: (showStartAnimation: boolean) => void
  setPrivacyUrl: (privacyUrl: string) => void
  setEmbedFeature: (embedFeature: EMBED_FEATURE, embedAutoHeight?: boolean) => void
  setActiveTemplate: (activeTemplate?: GrowthTemplateUnserialized) => void
  setActiveTemplateStep: (activeTemplateStep: TemplateStepType) => void
  setActiveModule: (activeModule: WIDGET_MODULE_TYPE) => void
  markUserActivatedModule: () => void
  setLazyComponentsLoaded: (component: WidgetModule, value: boolean) => void

  // message store
  clearEntries: () => void
  addEntry: (entry: Entry) => void
  setEntries: (entries: Entry[]) => void
  setEntryStatus: (entryId: string, status: 'sending' | 'failed' | 'sent') => void
  registerOptimisticEntry: (id: string) => void
  resolveOptimisticEntry: (id: string) => void
  setIsTypingIndicator: (isTypingIndicator: boolean) => void
  setIsConnected: (isConnected: boolean) => void
  incrementUnreadCount: () => void
  resetUnreadCount: () => void

  injectLiveModeActions: (name: keyof LiveModeActions, action: () => void) => void
  setIsInitialMessageSent: (isConnected: boolean) => void
  setIsInitialAnimationDone: (isInitialAnimationDone: boolean) => void

  // audio store
  setCurrentlyPlayingAudio: (newAudio: HTMLAudioElement) => void
  stopCurrentlyPlayingAudio: () => void

  // forwarding page
  openForwardingPage: (channel: WIDGET_CHANNEL) => void

  // sms subscription
  openSms: () => void

  // forwarding
  setSelectedChannel: (channel?: keyof WIDGET_CHANNEL) => void

  // Viewport
  setParentViewportSize: (size: { width: number; height: number } | null) => void
} & LiveModeActions

const initialState: State = {
  isPersistentStore: true,
  region: 'au',
  env: import.meta.env.MODE === 'production' ? 'prd' : 'stg',
  projectConfig: undefined,
  userIdentity: undefined,
  isPreview: false,
  embedFeature: null,
  embedAutoHeight: false,
  isOpen: false,
  showPrivacyCheck: false,
  isStartScreenSubmitted: false,
  isInitialAnimationDone: false,
  showBannerContent: false,
  entries: [],
  entryStatuses: {},
  pendingOptimisticIds: [],
  isTypingIndicator: false,
  isAgentInConversation: false,
  isConnected: false,
  unreadCount: 0,
  showEndConversationOverlay: false,
  isAvailable: true,
  isInitialMessageSent: false,
  currentlyPlayingAudio: null,
  enabledSocialChannels: [],
  showStartAnimation: false,
  privacyUrl: '',
  allIdentities: {},
  activeModule: 'livechat',
  hasUserActivatedModule: false,
  lazyComponentsLoaded: {},
  activeTemplateStep: 'attentionGrabber',
  forwardingPageChannel: undefined,
  parentViewportSize: null,
}

export const useWebWidgetStore = create<State & Actions>()(
  devtools(
    persist(
      bigIntMiddleware((set, get) => ({
        ...initialState,
        setWidget: widget => set(() => ({ widget, widgetId: widget.id })),
        setLiveChatOption: (name, value) =>
          set(state => {
            const widget = { ...state.widget }
            if (!widget.channels?.['livechat']) {
              return state
            }
            widget.channels['livechat'][name] = value
            return {
              widget,
            }
          }),
        setIsPersistentStore: isPersistentStore => set(() => ({ isPersistentStore })),
        setRegion: region => set(() => ({ region: region.toLowerCase() })),
        setEnv: env => set(() => ({ env: env.toLowerCase() })),
        // keep all identities during reset. they need to be removed via closeConversation call
        //
        // Preserve embedFeature/isOpen/activeModule the same way closeConversation
        // does below, rather than nulling them via ...initialState. reset() is only
        // safe against an already-mounted, live embed session today because the
        // parent loader always tears down and recreates the entire iframe document
        // before initializing a different widget — but that's a fragile invariant to
        // rely on silently, especially since there's a real `await fetch(...)`
        // network round-trip between reset() and the subsequent setEmbedFeature()
        // call in iframe-app.tsx's initializeWidget, not a synchronous gap. Outside
        // embed mode this is a complete no-op (state.embedFeature is null, so all
        // three fields reproduce exactly what ...initialState already provided).
        //
        // The parent viewport describes the host page, not the widget, so it survives a reset:
        // the loader only sends it once on ready, and losing it makes breakpoints fall back
        // to the (tiny) iframe window.
        reset: () =>
          set(state => ({
            ...initialState,
            allIdentities: state.allIdentities,
            embedFeature: state.embedFeature,
            embedAutoHeight: state.embedAutoHeight,
            isOpen: state.embedFeature ? true : initialState.isOpen,
            activeModule: state.embedFeature ?? initialState.activeModule,
            parentViewportSize: state.parentViewportSize,
          })),
        closeConversation: () =>
          set(state => {
            const currentIdentity = state.widget?.id || '0'
            const { [currentIdentity]: _, ...otherIdentities } = state.allIdentities
            return {
              ...initialState,
              allIdentities: otherIdentities,
              projectConfig: state.projectConfig,
              isPreview: state.isPreview,
              isPersistentStore: state.isPersistentStore,
              isInitialAnimationDone: state.isInitialAnimationDone,
              embedFeature: state.embedFeature,
              embedAutoHeight: state.embedAutoHeight,
              isOpen: state.embedFeature ? true : initialState.isOpen,
              // If in embed mode, activeModule should always equal embedFeature —
              // the same invariant setEmbedFeature establishes — otherwise fall
              // back to the original initialState.activeModule. Without this,
              // activeModule silently reset to 'livechat' even for a growth embed;
              // harmless today only because closeConversation is unreachable from
              // a mounted growth embed, but a latent trap if that ever changes.
              activeModule: state.embedFeature ?? initialState.activeModule,
            }
          }),
        setProjectConfig: projectConfig => set(() => ({ projectConfig })),
        setPreview: isPreview => set(() => ({ isPreview })),
        setToken: token =>
          set(state => ({ allIdentities: { ...state.allIdentities, [state.widget?.id || '0']: token } })),
        getToken: () => {
          const state = get()
          return state.allIdentities[state.widget?.id || '0']
        },
        setUserIdentity: userIdentity => set(() => ({ userIdentity })),
        open: () => set(() => ({ isOpen: true })),
        close: () => set(state => (state.embedFeature ? state : { isOpen: false })),
        toggle: () => set(state => (state.embedFeature ? state : { isOpen: !state.isOpen })),
        setShowPrivacyCheck: showPrivacyCheck => set(() => ({ showPrivacyCheck })),
        setIsStartScreenSubmitted: isStartScreenSubmitted => set(() => ({ isStartScreenSubmitted })),
        setShowBannerContent: showBannerContent => set(() => ({ showBannerContent })),
        setShowEndConversationOverlay: showEndConversationOverlay => set(() => ({ showEndConversationOverlay })),
        setAttachments: attachments => set(() => ({ attachments })),
        clearEntries: () => set(() => ({ entries: [], entryStatuses: {}, pendingOptimisticIds: [] })),
        addEntry: (entry: Entry) => {
          const newState = [...(get().entries || []), entry]
          set({
            entries: newState,
            isAgentInConversation: getIsAgentInConversation(newState),
          })
        },
        setEntries: (entries: Entry[]) => {
          set({
            entries,
            isAgentInConversation: getIsAgentInConversation(entries),
          })
        },
        setEntryStatus: (entryId, status) => {
          if (status === 'sent') {
            const { [entryId]: _, ...rest } = get().entryStatuses
            set({ entryStatuses: rest })
          } else {
            set(state => ({ entryStatuses: { ...state.entryStatuses, [entryId]: status } }))
          }
        },
        registerOptimisticEntry: id => set(state => ({ pendingOptimisticIds: [...state.pendingOptimisticIds, id] })),
        resolveOptimisticEntry: id =>
          set(state => ({ pendingOptimisticIds: state.pendingOptimisticIds.filter(x => x !== id) })),
        setIsTypingIndicator: isTypingIndicator =>
          setIsTypingWithTimeout(isTypingIndicator, isTypingIndicator => set({ isTypingIndicator })),
        setIsConnected: isConnected => set(() => ({ isConnected })),
        setIsAvailable: isAvailable => set(() => ({ isAvailable })),
        setEnabledSocialChannels: enabledSocialChannels => set(() => ({ enabledSocialChannels })),
        setShowStartAnimation: showStartAnimation => set(() => ({ showStartAnimation })),
        setPrivacyUrl: privacyUrl => set(() => ({ privacyUrl })),
        setEmbedFeature: (embedFeature, embedAutoHeight) =>
          set(() => ({ embedFeature, embedAutoHeight: !!embedAutoHeight, activeModule: embedFeature, isOpen: true })),
        setActiveModule: activeModule => set(() => ({ activeModule })),
        // Sticky: only ever flips false -> true, from the Launcher's user
        // click handler. Never called by the auto-open timer or the
        // persisted-isOpen restore path, so those keep it false.
        markUserActivatedModule: () => set(() => ({ hasUserActivatedModule: true })),
        setActiveTemplate: activeTemplate => set(() => ({ activeTemplate })),
        setActiveTemplateStep: (activeTemplateStep: TemplateStepType) => set(() => ({ activeTemplateStep })),
        injectLiveModeActions: (name, method) => set(() => ({ [name]: method })),
        setIsInitialMessageSent: isInitialMessageSent => set(() => ({ isInitialMessageSent })),
        setIsInitialAnimationDone: isInitialAnimationDone => set(() => ({ isInitialAnimationDone })),
        setCurrentlyPlayingAudio: newAudio =>
          set(state => {
            // If a different audio is currently playing, pause it
            if (state.currentlyPlayingAudio && state.currentlyPlayingAudio !== newAudio) {
              state.currentlyPlayingAudio.pause()
            }

            // Set the new audio as currently playing
            return { currentlyPlayingAudio: newAudio }
          }),

        // Stops the current audio (pause and reset to 0)
        stopCurrentlyPlayingAudio: () =>
          set(state => {
            if (state.currentlyPlayingAudio) {
              state.currentlyPlayingAudio.pause()
              state.currentlyPlayingAudio.currentTime = 0 // Optionally reset
            }
            return { currentlyPlayingAudio: null }
          }),
        openForwardingPage: channel =>
          set(() => ({ activeModule: 'forwarding-page', forwardingPageChannel: channel, isOpen: true })),
        openSms: () => set(() => ({ activeModule: 'sms', isOpen: true })),
        setSelectedChannel: channel => set(() => ({ selectedChannel: channel })),
        incrementUnreadCount: () => set(state => ({ unreadCount: state.unreadCount + 1 })),
        resetUnreadCount: () => set(() => ({ unreadCount: 0 })),
        setParentViewportSize: size => set(() => ({ parentViewportSize: size })),
        setLazyComponentsLoaded: component =>
          set(state => ({
            ...state,
            lazyComponentsLoaded: {
              ...state.lazyComponentsLoaded,
              [component]: true,
            },
          })),
      })),
      {
        version: 2,
        name: `seww`,
        // only in persistent mode
        partialize: state => {
          if (!state.isPersistentStore) {
            return {}
          }
          const isEmbedMode = !!state.embedFeature
          return Object.fromEntries(
            Object.entries(state).filter(([key]) => {
              if (
                [
                  'lazyComponentsLoaded',
                  'isPersistentStore',
                  'entries',
                  'entryStatuses',
                  'pendingOptimisticIds',
                  'isAvailable',
                  'isTypingIndicator',
                  'isAgentInConversation',
                  'isConnected',
                  'attachments',
                  'currentlyPlayingAudio',
                  'parentViewportSize',
                  // Must stay false on every fresh page load so an auto/persisted
                  // open never triggers module auto-focus (host-page focus-steal
                  // guard). Only a real user gesture this session sets it true.
                  'hasUserActivatedModule',
                  // Never rehydrate: a later non-embed page load on the same
                  // widgetId (no store.reset()) must not resurrect a stale embed.
                  'embedFeature',
                  'embedAutoHeight',
                ].includes(key)
              ) {
                return false
              }
              // Only skip persisting isOpen while writing from an embed session,
              // so setEmbedFeature's forced isOpen:true never bleeds into a later
              // non-embed session's persisted state. Non-embed isOpen persistence
              // (e.g. surviving a page navigation) is intentionally untouched.
              if (isEmbedMode && key === 'isOpen') {
                return false
              }
              return true
            }),
          )
        },
      },
    ),
  ),
)

export type WebWidgetStore = ReturnType<typeof useWebWidgetStore>
